<template>
  <div
    class="min-vh-100 d-flex align-items-center bg-image-full d-flex align-items-center justify-content-center"
    :style="`background-image: url(${background})`"
  >
    <div class="container-lg text-white">
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 col-xl-6 p-0">
          <div
            class="login-box rounded rounded-0-sm p-3 position-relative bg-primary bg-opacity-75 border-gradient-corner-secondary-1"
          >
            <div class="position-absolute top-0 end-0 mt-2 me-2">
              <data-bs-theme></data-bs-theme>
            </div>

            <div class="text-center">
              <img
                :src="getLogo"
                :alt="$t('main.company.name')"
                class="logo-img col-7 col-md-6"
                :onerror="onerror"
              />
              <div class="logo-text">{{ $t("main.app") }}</div>
              <TitleDivider class="my-3" />
              <div class="logo-subtitle text-muted small mt-2 mb-3">
                {{ $t("main.login.app") }}
              </div>
            </div>

            <login-modal @onLogin="continueUse"></login-modal>

            <div class="fs-6 text-center border-top border-secondary mt-4 pt-3">
              <a
                :href="`http://${staff_href}/login-staff.html`"
                class="text-decoration-none text-muted"
                v-t="'internet.staff'"
              ></a>
              <div class="mt-1">POWERED BY FILOXENIA</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { mixins, Options } from "vue-class-component";
import { useAuthorizationStore } from "@/store/modules/authorization";
import { onLogin } from "@/api/authorization/user-authorized-module";
import { onRegistrationTicket } from "@/api/authorization/user-authorized-module";
import { scheduleProactiveRefresh } from "@/api/authorization/auth-request-api";
import { getImage } from "@/services/getImage";
import { theme } from "@/store/modules/theme";
import { configurations } from "@/store/modules/hotel/configuration";
import { auth, configuration } from "@/config/host.config";
import { useVRoom } from "@/store/modules/hotel/virtual-room";
import { getStaffHref } from "@/config/host/mikrotik-staff";

import axios from "axios";
import LoginModal from "@/components/login/login-modal.vue";
import UserAgent from "@/mixins/login/user-agent";
import DataBsTheme from "@/components/app/data-bs-theme.vue";
import TitleDivider from "@/components/app/_shared/dividers/TitleDivider.vue";

import type { AxiosResponse, AxiosError } from "axios";
import type { VRooma } from "@/@types/vroom/virtualRooma";
import type { IAuth } from "@/@types/auth/auth";

interface LogoRes {
  configurationmobile: {
    background: string;
    logo: string;
  };
}

@Options({
  name: "Hello",
  components: { DataBsTheme, LoginModal, TitleDivider },
})
class LoginView extends mixins(UserAgent) {
  bgFolder = "main/configuration/background/mobile";
  logoFolder = "main/configuration/logo/mobile";

  width = window.innerWidth;
  spinnerShow = false;
  showAutoTransition = false;
  theme = theme();

  get staff_href(): string {
    return getStaffHref();
  }

  autologinStarted = false;
  deviceWaitTimeout!: ReturnType<typeof setTimeout>;

  onerror = `this.onerror=null; this.src='${getImage("filoxenia logo.png", null)}';`;

  get background(): string | null {
    const bg = configurations().getBackground;
    if (bg) {
      const folder = this.width < 600 ? this.bgFolder : `${this.bgFolder}/full`;
      return this.$getWebImage(bg, folder);
    }
    return "";
  }

  get getLogo(): string {
    const logo = configurations().getLogo;
    if (logo) return this.$getWebImage(logo, this.logoFolder);
    const img = getImage("filoxenia logo.png", null);
    return img ? img : "";
  }

  private resizeHandler = () => {
    this.width = window.innerWidth;
  };

  mounted(): void {
    this.$nextTick(() => this.waitDeviceId());
    this.createConfiguration();
    this.open();
    window.addEventListener("resize", this.resizeHandler);
  }

  unmounted(): void {
    clearTimeout(this.deviceWaitTimeout);
    window.removeEventListener("resize", this.resizeHandler);
  }

  waitDeviceId(attempt = 0): void {
    const MAX_ATTEMPTS = 10;
    const DELAY_MS = 1000;
    if (this.autologinStarted) return;

    const device_id = localStorage.getItem("device_id") || "";
    if (device_id) {
      this.autologinStarted = true;
      this.autologin();
      return;
    }

    if (attempt < MAX_ATTEMPTS - 1) {
      this.deviceWaitTimeout = setTimeout(() => this.waitDeviceId(attempt + 1), DELAY_MS);
    }
  }

  async createConfiguration(): Promise<void> {
    try {
      const res: AxiosResponse<LogoRes> = await axios.get(configuration.hello.free_logo);
      if (res.status === 200) {
        configurations().setLogo(res.data.configurationmobile.logo);
        configurations().setBackground(res.data.configurationmobile.background);
      }
    } catch (error) {
      this.$error(error);
    }
  }

  async gethello(): Promise<void> {
    try {
      const res: AxiosResponse<LogoRes> = await axios.get(configuration.hello.free_logo);
      if (res.status === 200) {
        configurations().setLogo(res.data.configurationmobile.logo);
        configurations().setBackground(res.data.configurationmobile.background);
      }
    } catch (error) {
      this.$error(error);
    }
  }

  async myVirtualRooma(): Promise<VRooma | null> {
    try {
      const res = await this.$axios.get(auth.myrooms);
      if (res.status === 200) {
        useVRoom().setRvooma(res.data);
        return res.data; // Возвращаем данные для использования в .then()
      }
      return null; // или можно выбросить ошибку, если status не 200
    } catch (e) {
      this.$error(e);
      return null;
    }
  }

  continueUse(
    login = "guest",
    password = "guest",
    onFail?: (status?: number) => void,
    appendPassword = true
  ): void {
    if (appendPassword && !["guest", "root", "sem"].includes(login)) {
      login += password;
      this.$cookies.set("vr_pas", password);
    }

    login = String(login);
    password = String(password);
    onLogin({ login, password })
      .then((data: IAuth) => {
        const auth = useAuthorizationStore();
        auth.setTokens(data.access, data.refreshtoken);
        scheduleProactiveRefresh();
        this.$emit("strat");
        this.myVirtualRooma().finally(() => {
          if (!useVRoom().isHello) this.$router.replace({ name: "hello" });
          else this.$router.push({ name: "home" });
        });
      })
      .catch((e: AxiosError) => {
        this.$error(e);
        let text = "error.login.empty";
        if (e && e.response) text = `error.login.${e.response.status}`;
        console.log(text);
        if (onFail) onFail(e?.response?.status);
      });
  }

  open(): void {
    const refresh = localStorage.getItem("refreshToken");
    const notFirstTime = localStorage.getItem("not-first-time");
    if (refresh && notFirstTime) {
      this.$router.push({ name: "home" });
      return;
    }
    localStorage.setItem("not-first-time", "1");
  }

  autologin(): void {
    const device_id = localStorage.getItem("device_id") || "";
    if (!device_id) return;
    this.continueUse(
      device_id,
      "filoxenia-anabella-22",
      (status) => {
        if (status === 400) {
          onRegistrationTicket(device_id).catch((e: AxiosError) => this.$error(e));
        }
      },
      false
    );
  }
}
export default LoginView;
</script>
<style lang="scss">
@import "@/assets/sass/variables.scss";
.login-box {
  box-shadow: 0 5px 20px rgba($black, 0.08);
  border: 1px solid rgba($warning, 0.3);
  transition: all 0.3s ease;
}

.logo-img {
  margin-bottom: 1rem;
  filter: brightness(0.9);
  transition: filter 0.3s ease;
}

[data-bs-theme="dark"] .logo-img {
  filter: brightness(1.2);
}

.logo-text {
  font-size: 1.8rem;
  font-weight: 600;
  transition: color 0.3s ease;
}

.logo-subtitle {
  font-size: 0.875rem;
}

.form-group {
  margin-bottom: 1.8rem;
}

.input-hint {
  font-size: 0.85rem;
  margin-top: 0.4rem;
}

.login-box {
  animation: slideUp 0.5s ease;
  position: relative;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
